import {getPreviewRequestHeaders} from '@tlg-contenthub/live-preview';

import {fetch, isServer, getConfig, timeout} from 'coreUtils/utils';
import {getAppOrigin} from 'coreUtils/url';
import {registerModuleStateReset} from 'coreUtils/moduleState';
import {CPA_PROXY_ROUTE, getContentHubBaseUrl, getDefaultContentHubConnection} from 'coreUtils/cms/contentHubConnection';
import type {IContentHubConnection} from 'coreUtils/cms/contentHubConnection';
import {IContentHubClientConfig, IContentHubResponse, IContentHubResponseMeta} from './IContentHubClient';
import {buildEntriesUrl} from './entriesQuery';

const MAX_RETRIES = 2;
const RETRYABLE_STATUSES = new Set([429, 503]);

let deliveryClient: ContentHubClient = null;
let previewClient: ContentHubClient = null;

registerModuleStateReset(() => {
	deliveryClient = null;
	previewClient = null;
});

/**
 * Lightweight ContentHub v2 connector that replaces the `contentful` npm SDK.
 * Server-side: calls the ContentHub host directly.
 * Client-side: the delivery API directly, the preview API through the CPA proxy of the server
 * (`CPA_PROXY_ROUTE`), which adds the token: the browser never holds it.
 *
 * Retries automatically on 429 (rate limit) and 503 (transient) responses,
 * honouring the Retry-After header with exponential backoff as fallback.
 */
class ContentHubClient {
	private readonly connection: IContentHubConnection;
	private readonly proxyPath: string;
	private readonly debug: boolean;

	/**
	 * Constructor
	 * @param {IContentHubClientConfig} config - config
	 */
	constructor({connection, proxyPath, debug = false}: IContentHubClientConfig) {
		this.connection = connection;
		this.proxyPath = proxyPath;
		this.debug = debug;
	}

	/**
	 * Fetches entries from the ContentHub v2 API, equivalent to the contentful SDK's getEntries.
	 * Retries up to MAX_RETRIES times on 429/503, honouring the Retry-After header.
	 */
	async getEntries(query: Record<string, any> = {}): Promise<IContentHubResponse> {
		const url = this.buildUrl(query);
		const headers = this.buildHeaders();

		let lastResponse: any = null;

		for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
			if (attempt > 0 && lastResponse) {
				const delay = ContentHubClient.getRetryDelay(lastResponse, attempt);

				await timeout(delay);
			}

			const response: any = await fetch(url, {headers});
			lastResponse = response;

			if (response.ok) {
				const body: IContentHubResponse = await response.json();

				body._meta = ContentHubClient.extractMeta(response);

				return body;
			}

			if (RETRYABLE_STATUSES.has(response.status)) {
				continue;
			}

			throw new Error(`ContentHub request failed: ${response.status} ${response.statusText}`);
		}

		throw new Error(
			`ContentHub request failed after ${MAX_RETRIES + 1} attempts: ${lastResponse?.status} ${lastResponse?.statusText}`,
		);
	}

	/**
	 * Url Builder
	 * @param query
	 */
	private buildUrl(query: Record<string, any>): string {
		let baseUrl: string;

		if (isServer || !this.proxyPath) {
			baseUrl = `${getContentHubBaseUrl(this.connection)}/entries`;
		} else {
			baseUrl = `${getAppOrigin()}${this.proxyPath}/entries`;
		}

		return buildEntriesUrl(baseUrl, query);
	}

	/**
	 * Headers builder
	 */
	private buildHeaders(): Record<string, string> {
		const headers: Record<string, string> = {};

		if (this.connection.token) {
			headers['Authorization'] = `Bearer ${this.connection.token}`;
		}

		if (isServer) {
			headers['Content-Type'] = 'application/json';
		}

		if (this.debug) {
			headers['X-TLG-Show-CacheTags'] = '1';
		}

		// Inside the CMS live preview, after an edit: skip the CPA cache and read at least
		// the edited state (empty otherwise, so normal browsing keeps the cache).
		if (!isServer) {
			Object.assign(headers, getPreviewRequestHeaders());
		}

		return headers;
	}

	/**
	 * Extracts debugging meta data from http headers
	 * @param response
	 */
	private static extractMeta(response: any): IContentHubResponseMeta {
		const h = (name: string): string | null => response.headers?.get?.(name) ?? null;

		const meta: IContentHubResponseMeta = {
			status: response.status,
		};

		const cache = h('X-Cache');

		if (cache) {
			meta.cache = cache as IContentHubResponseMeta['cache'];
		}

		const dbSource = h('X-TLG-DB-Source');

		if (dbSource) {
			meta.dbSource = dbSource;
		}

		const coldStart = h('X-Cold-Start');

		if (coldStart !== null) {
			meta.coldStart = coldStart === '1';
		}

		const retryAfter = h('Retry-After');

		if (retryAfter) {
			meta.retryAfter = Number(retryAfter);
		}

		const warning = h('Warning');

		if (warning) {
			meta.warning = warning;
		}

		const cacheTags = h('X-Cache-Tags');

		if (cacheTags) {
			meta.cacheTags = cacheTags.split(',').map((t) => t.trim());
		}

		return meta;
	}

	/**
	 * Computes retry delay from the Retry-After header, falling back to
	 * exponential backoff: 1s, 2s, 4s (capped).
	 */
	private static getRetryDelay(response: any, attempt: number): number {
		const retryAfter = response.headers?.get?.('Retry-After');

		if (retryAfter) {
			const seconds = Number(retryAfter);

			if (!Number.isNaN(seconds) && seconds > 0) {
				return seconds * 1000;
			}
		}

		return Math.min(1000 * Math.pow(2, attempt - 1), 4000);
	}

	/**
	 * Returns the delivery or preview ContentHub client of the instance (lazily created), or on the server a
	 * client for the CPA connection of the request.
	 * @param {boolean} preview - Preview API (CPA)
	 * @param {IContentHubConnection} [cpaConnection] - Server side, CPA connection of the request (instance
	 * defaults or DevToolkit override, see `server/cms/contentHubConnection.ts`)
	 */
	static getClient(preview = false, cpaConnection?: IContentHubConnection): ContentHubClient {
		if (preview && isServer && cpaConnection) {
			return new ContentHubClient({connection: cpaConnection});
		}

		if (preview) {
			if (!previewClient) {
				previewClient = ContentHubClient.createFromConfig(true);
			}

			return previewClient;
		}

		if (!deliveryClient) {
			deliveryClient = ContentHubClient.createFromConfig(false);
		}

		return deliveryClient;
	}

	/**
	 * Factory from config: connection of the instance; in the browser, the preview goes through the CPA proxy,
	 * which picks host, version, space, environment and token for the session.
	 * @param {Boolean} preview - true if preview
	 * @private
	 */
	private static createFromConfig(preview: boolean): ContentHubClient {
		// Env vars (hosts, tokens, a local Content Hub) only exist on the server
		const connection = getDefaultContentHubConnection(preview, getConfig().app.cmsAPI.parameters, isServer ? process.env : {});

		return new ContentHubClient({connection, proxyPath: preview && !isServer ? CPA_PROXY_ROUTE : undefined});
	}
}

export default ContentHubClient;
